import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
export { API_BASE };

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const isGuide = window.location.pathname.startsWith('/guide');
    const token = isGuide
        ? localStorage.getItem('guide_token')
        : localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// fetch natif avec le header Authorization (même logique de token que l'intercepteur axios)
export const authFetch = (url, options = {}) => {
    const isGuide = window.location.pathname.startsWith('/guide');
    const token = isGuide ? localStorage.getItem('guide_token') : localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
};

// Ouvre un PDF protégé (fetch avec token → blob → nouvel onglet)
export const openAuthPdf = async (url, errMsg = 'Erreur lors de la génération du PDF') => {
    const resp = await authFetch(url);
    if (!resp.ok) {
        const { toast } = await import('sonner');
        let detail = errMsg;
        try { detail = (await resp.json()).detail || errMsg; } catch { /* ignore */ }
        toast.error(detail);
        return;
    }
    const blob = await resp.blob();
    window.open(URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })), '_blank');
};

// Télécharge un fichier protégé (fetch avec token → blob → lien de téléchargement)
export const downloadAuthFile = async (url, fallbackName = 'export') => {
    const resp = await authFetch(url);
    if (!resp.ok) {
        const { toast } = await import('sonner');
        let detail = 'Erreur lors du téléchargement';
        try { detail = (await resp.json()).detail || detail; } catch { /* ignore */ }
        toast.error(detail);
        return;
    }
    const blob = await resp.blob();
    const cd = resp.headers.get('content-disposition') || '';
    const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = m ? decodeURIComponent(m[1]) : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 403 && error.response?.data?.code === 'password_change_required') {
            // Token « mot de passe provisoire » : retour au login pour forcer le changement
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (!window.location.pathname.startsWith('/login')) {
                window.location.href = '/login?pwd=1';
            }
            return Promise.reject(error);
        }
        if (error.response?.status === 401) {
            // Check if we're in the Guide context
            const isGuide = window.location.pathname.startsWith('/guide');
            if (isGuide) {
                localStorage.removeItem('guide_token');
                localStorage.removeItem('guide_user');
                window.location.href = '/guide/login';
            } else {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    getLoginLogs: (params) => api.get('/auth/login-logs', { params }),
    trackPageView: (path) => api.post('/auth/page-view', { path }),
    getPageViews: (params) => api.get('/auth/page-views', { params }),
    renameDevice: (deviceId, name) => api.patch(`/auth/devices/${deviceId}`, { name }),
    getDevices: () => api.get('/auth/devices'),
    setDeviceApproval: (deviceId, approved) => api.put(`/auth/devices/${deviceId}/approval`, { approved }),
    deleteDevice: (deviceId) => api.delete(`/auth/devices/${deviceId}`),
    dedupeDevices: () => api.post('/auth/devices/dedupe'),
    getDeviceControl: () => api.get('/auth/device-control'),
    setDeviceControl: (data) => api.put('/auth/device-control', data),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me', { headers: { Authorization: localStorage.getItem('token') } }),
};

// Users Management
export const usersAPI = {
    getAll: () => api.get('/users'),
    getById: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    resetPassword: (id, newPassword) => api.put(`/users/${id}/password?new_password=${encodeURIComponent(newPassword)}`),
};

// Permissions Management
export const permissionsAPI = {
    getAvailable: () => api.get('/permissions/available'),
    getAllRoles: () => api.get('/permissions/roles'),
    getRolePermissions: (role) => api.get(`/permissions/roles/${role}`),
    updateRolePermissions: (role, permissions) => api.put(`/permissions/roles/${role}`, { role, permissions }),
    resetRolePermissions: (role) => api.delete(`/permissions/roles/${role}`),
};

// Sites
export const sitesAPI = {
    getAll: () => api.get('/sites'),
    create: (data) => api.post('/sites', data),
    update: (id, data) => api.put(`/sites/${id}`, data),
    delete: (id) => api.delete(`/sites/${id}`),
    uploadIcon: (id, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/sites/${id}/icon`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteIcon: (id) => api.delete(`/sites/${id}/icon`),
    getInventoryFormPdfUrl: (id, period, zones) => {
        let url = `${API_BASE}/sites/${id}/inventory-form/pdf`;
        const params = [];
        if (period) params.push(`period=${encodeURIComponent(period)}`);
        if (zones) params.push(`zones=${encodeURIComponent(zones)}`);
        if (params.length > 0) url += `?${params.join('&')}`;
        return url;
    },
    getInventoryFormFromMovementsPdfUrl: (id, period) => {
        let url = `${API_BASE}/sites/${id}/inventory-form/from-movements/pdf`;
        if (period) url += `?period=${encodeURIComponent(period)}`;
        return url;
    },
};

// Suppliers
export const suppliersAPI = {
    getAll: () => api.get('/suppliers'),
    create: (data) => api.post('/suppliers', data),
    update: (id, data) => api.put(`/suppliers/${id}`, data),
    delete: (id) => api.delete(`/suppliers/${id}`),
    getProducts: (id) => api.get(`/suppliers/${id}/products`),
    getMercurialePdfUrl: (id) => `${API_BASE}/suppliers/${id}/mercuriale/pdf`,
    getEmailTestMode: () => api.get('/suppliers/email-test-mode'),
    setEmailTestMode: (data) => api.put('/suppliers/email-test-mode', data),
};

// Industrial Products
export const industrialProductsAPI = {
    getAll: (category, famille) => api.get('/industrial-products', { params: { category, famille } }),
    getCategories: () => api.get('/industrial-products/categories'),
    getFamilles: () => api.get('/industrial-products/familles'),
    addFamille: (famille) => api.post('/industrial-products/familles', { famille }),
    deleteFamille: (famille) => api.delete(`/industrial-products/familles/${encodeURIComponent(famille)}`),
    create: (data) => api.post('/industrial-products', data),
    update: (id, data) => api.put(`/industrial-products/${id}`, data),
    delete: (id) => api.delete(`/industrial-products/${id}`),
    bulkUpdate: (ids, updates) => api.put('/industrial-products/bulk-update', { ids, updates }),
    uploadImage: (id, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/industrial-products/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteImage: (id) => api.delete(`/industrial-products/${id}/image`),
    copyImage: (id, sourceType, sourceId) => api.post(`/industrial-products/${id}/image/copy`, { source_type: sourceType, source_id: sourceId }),
};

// Supplier Products
export const supplierProductsAPI = {
    getAll: (industrialProductId) => api.get('/supplier-products', { params: { industrial_product_id: industrialProductId } }),
    getDetail: (id) => api.get(`/supplier-products/detail/${id}`),
    create: (data) => api.post('/supplier-products', data),
    update: (id, data) => api.put(`/supplier-products/${id}`, data),
    delete: (id) => api.delete(`/supplier-products/${id}`),
    toggleCommandable: (id, commandable) => api.patch(`/supplier-products/${id}/commandable`, { commandable }),
    toggleCompare: (id, compare) => api.patch(`/supplier-products/${id}/compare`, { compare }),
    getComparison: () => api.get('/supplier-products/comparison'),
    clearComparison: () => api.post('/supplier-products/comparison/clear'),
    move: (id, newIndustrialProductId) => api.patch(`/supplier-products/${id}/move`, { new_industrial_product_id: newIndustrialProductId }),
    uploadImage: (id, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/supplier-products/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteImage: (id) => api.delete(`/supplier-products/${id}/image`),
    copyImage: (id, sourceType, sourceId) => api.post(`/supplier-products/${id}/image/copy`, { source_type: sourceType, source_id: sourceId }),
    bulkUpdate: (ids, updates) => api.put('/supplier-products/bulk-update', { ids, updates }),
};

// Orders
export const ordersAPI = {
    getAll: (params) => api.get('/orders', { params }),
    create: (data) => api.post('/orders', data),
    updateStatus: (id, status) => api.put(`/orders/${id}/status?status=${status}`),
    updateItems: (id, data) => api.put(`/orders/${id}/items`, data),
    update: (id, data) => api.put(`/orders/${id}/items`, data),
    delete: (id) => api.delete(`/orders/${id}`),
    sendEmail: (id) => api.post(`/orders/${id}/send-email`),
    sendWhatsApp: (id, data) => api.post(`/orders/${id}/whatsapp`, data),
    receive: (id, data) => api.post(`/orders/${id}/receive`, data),
    closeRemaining: (id) => api.post(`/orders/${id}/close-remaining`),
    getReceptionHistory: (id) => api.get(`/orders/${id}/reception-history`),
    updateReception: (id, data) => api.put(`/orders/${id}/reception`, data),
    deleteReception: (orderId, movementId) => api.delete(`/orders/${orderId}/reception/${movementId}`),
    // Reception draft (work-in-progress)
    getReceptionDraft: (id) => api.get(`/orders/${id}/reception-draft`),
    saveReceptionDraft: (id, data) => api.put(`/orders/${id}/reception-draft`, data),
    deleteReceptionDraft: (id) => api.delete(`/orders/${id}/reception-draft`),
    getPdfUrl: (id) => `${API_BASE}/orders/${id}/pdf`,
    // Manual receptions
    getManualReceptions: () => api.get('/manual-receptions'),
    getManualReception: (id) => api.get(`/manual-receptions/${id}`),
    getOrderById: (id) => api.get(`/orders/by-id/${id}`),
    createManualReception: (data) => api.post('/manual-receptions', data),
    updateManualReception: (id, data) => api.put(`/manual-receptions/${id}`, data),
    deleteManualReception: (id) => api.delete(`/manual-receptions/${id}`),
};

export const receptionsAPI = {
    byProduct: (params) => api.get('/receptions/by-product', { params }),
    importCarrefourPreview: (formData) => api.post('/receptions/carrefour/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    importCarrefourPreviewStatus: (jobId) => api.get(`/receptions/carrefour/preview/status/${jobId}`),
    importCarrefourConfirm: (payload) => api.post('/receptions/carrefour/confirm', payload),
    // Generic invoice import (any supplier). Server auto-detects native vs scan
    // via PyMuPDF; pass force_scan='true' to override.
    importInvoicePreview: (formData) => api.post('/receptions/invoice/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    importInvoicePreviewStatus: (jobId) => api.get(`/receptions/invoice/preview/status/${jobId}`),
    importInvoiceConfirm: (payload) => api.post('/receptions/invoice/confirm', payload),
    invoiceReconcile: (payload) => api.post('/invoice-reception/reconcile', payload),
    invoiceReceptionCommit: (payload) => api.post('/invoice-reception/commit', payload),
};

// Reception Statistics
export const receptionStatsAPI = {
    get: (startDate, endDate, siteId, supplierId, siteIds) => api.get('/statistics/receptions', {
        params: {
            start_date: startDate,
            end_date: endDate,
            site_id: siteId,
            supplier_id: supplierId,
            site_ids: Array.isArray(siteIds) && siteIds.length > 0 ? siteIds.join(',') : undefined,
        }
    }),
    getEarliestDate: () => api.get('/statistics/receptions/earliest-date'),
};

// Finance (en compte tracking)
export const financeAPI = {
    list: (params) => api.get('/finance/items', { params }),
    updatePayment: (itemType, itemId, data) => api.patch(`/finance/items/${itemType}/${itemId}`, data),
    backfill: () => api.post('/finance/backfill'),
};

// Inventories
export const inventoriesAPI = {
    getAll: (params) => api.get('/inventories', { params }),
    create: (data) => api.post('/inventories', data),
    update: (id, data) => api.put(`/inventories/${id}`, data),
    delete: (id) => api.delete(`/inventories/${id}`),
    deleteGroup: (siteId, period, inventoryType) => api.delete(`/inventories/group/${siteId}/${period}/${inventoryType}`),
    getReceivedProductIds: () => api.get('/inventory/received-product-ids'),
    getReceivedBase: (siteId, period) => api.get('/inventory/received-base', { params: { site_id: siteId, period } }),
    getAuditLog: (params) => api.get('/inventory/audit-log', { params }),
};

// Inventory Form Templates (custom printable forms)
export const inventoryFormTemplatesAPI = {
    getAll: () => api.get('/inventory-form-templates'),
    getById: (id) => api.get(`/inventory-form-templates/${id}`),
    create: (data) => api.post('/inventory-form-templates', data),
    update: (id, data) => api.put(`/inventory-form-templates/${id}`, data),
    delete: (id) => api.delete(`/inventory-form-templates/${id}`),
    pdfUrl: (id, period) => {
        const base = `${process.env.REACT_APP_BACKEND_URL}/api/inventory-form-templates/${id}/pdf`;
        return period ? `${base}?period=${encodeURIComponent(period)}` : base;
    },
};

// Stock Movements
export const stockMovementsAPI = {
    getAll: (params) => api.get('/stock-movements', { params }),
    create: (data) => api.post('/stock-movements', data),
    delete: (id) => api.delete(`/stock-movements/${id}`),
};

// Price History
export const priceHistoryAPI = {
    getByProduct: (supplierProductId) => api.get(`/price-history/by-product/${supplierProductId}`),
    getSparklines: (ids, days = 180) => api.get('/price-history/sparklines', { params: { ids: ids.join(','), days } }),
    getAlerts: (threshold = 3, limit = 30) => api.get('/price-history/alerts', { params: { threshold, limit } }),
    rebuild: () => api.post('/price-history/rebuild'),
};

// Consumption
export const consumptionAPI = {
    get: (siteId, period) => api.get('/consumption', { params: { site_id: siteId, period } }),
    getMulti: (siteIds, period) => api.get('/consumption', { params: { site_ids: siteIds.join(','), period } }),
    getRange: (siteId, startPeriod, endPeriod) => api.get('/consumption/range', {
        params: { site_id: siteId, start_period: startPeriod, end_period: endPeriod }
    }),
    getRangeMulti: (siteIds, startPeriod, endPeriod) => api.get('/consumption/range', {
        params: { site_ids: siteIds.join(','), start_period: startPeriod, end_period: endPeriod }
    }),
};

// Statistics
export const statisticsAPI = {
    get: (params) => api.get('/statistics', { params }),
};

// Recipes
export const recipesAPI = {
    getAll: (params) => api.get('/recipes', { params }),
    getById: (id) => api.get(`/recipes/${id}`),
    create: (data) => api.post('/recipes', data),
    update: (id, data) => api.put(`/recipes/${id}`, data),
    delete: (id) => api.delete(`/recipes/${id}`),
    getCategories: () => api.get('/recipes/categories'),
    searchIngredients: (q, type) => api.get('/recipes/search-ingredients', { params: { q, ingredient_type: type } }),
    uploadImage: (id, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/recipes/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteImage: (id) => api.delete(`/recipes/${id}/image`),
    copyImage: (id, sourceId) => api.post(`/recipes/${id}/image/copy-from/${sourceId}`),
};

// Pricing Clients
export const pricingAPI = {
    getClients: () => api.get('/pricing-clients'),
    createClient: (data) => api.post('/pricing-clients', data),
    updateClient: (id, data) => api.put(`/pricing-clients/${id}`, data),
    deleteClient: (id) => api.delete(`/pricing-clients/${id}`),
    getClientPrices: (clientId) => api.get(`/pricing-clients/${clientId}/prices`),
    updateClientPrices: (clientId, prices) => api.put(`/pricing-clients/${clientId}/prices`, { prices }),
    comparison: () => api.get('/pricing-comparison'),
    comparisonPdf: (recipe_ids, columns) => api.post('/pricing-comparison/pdf', { recipe_ids, columns }, { responseType: 'blob' }),
    comparisonXlsx: (recipe_ids, columns) => api.post('/pricing-comparison/xlsx', { recipe_ids, columns }, { responseType: 'blob' }),
};

// Exports
export const exportAPI = {
    orders: (params) => `${API_BASE}/export/orders?${new URLSearchParams(params)}`,
    products: () => `${API_BASE}/export/products`,
    consumption: (siteId, period) => `${API_BASE}/export/consumption?site_id=${siteId}&period=${period}`,
    inventory: (params) => `${API_BASE}/export/inventory?${new URLSearchParams(params)}`,
    inventoryDetail: (siteId, period, inventoryType) => `${API_BASE}/export/inventory-detail?site_id=${siteId}&period=${period}&inventory_type=${inventoryType}`,
};

// Transfers
export const ratiosAPI = {
    month: (params) => api.get('/ratios/purchases/month', { params }),
    week: (params) => api.get('/ratios/purchases/week', { params }),
};

export const transfersAPI = {
    siegeReceptions: (params) => api.get('/transfers/siege-receptions', { params }),
    hideSiegeReception: (receptionId, hidden) => api.patch(`/transfers/siege-receptions/${receptionId}/hide`, { hidden }),
    getAll: (params) => api.get('/transfers', { params }),
    get: (id) => api.get(`/transfers/${id}`),
    create: (data) => api.post('/transfers', data),
    update: (id, data) => api.put(`/transfers/${id}`, data),
    validate: (id) => api.put(`/transfers/${id}/validate`),
    reject: (id, reason) => api.put(`/transfers/${id}/reject`, { reason }),
    delete: (id) => api.delete(`/transfers/${id}`),
    getMonthlyPairs: (period) => api.get('/transfer-recap/pairs', { params: { period } }),
    getMonthlyRecapDetail: (period, fromCompany, toCompany) =>
        api.get('/transfer-recap/detail', { params: { period, from_company: fromCompany, to_company: toCompany } }),
    getMonthlyRecapPdfUrl: (period, fromCompany, toCompany) => {
        const qs = new URLSearchParams({ period, from_company: fromCompany, to_company: toCompany }).toString();
        return `${API_BASE}/transfer-recap/pdf?${qs}`;
    },
};

export default api;

// Sales (vente)
export const salesAPI = {
    preview: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post('/sales/imports/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    confirm: (payload) => api.post('/sales/imports/confirm', payload),
    listImports: (params) => api.get('/sales/imports', { params }),
    listEntries: (params) => api.get('/sales/entries', { params }),
    deleteEntries: (params) => api.delete('/sales/entries', { params }),
    listAliases: () => api.get('/sales/meti-aliases'),
    coverage: (params) => api.get('/sales/coverage', { params }),
    upsertAlias: (codeMeti, data) => api.put(`/sales/meti-aliases/${codeMeti}`, data),
    deleteAlias: (codeMeti) => api.delete(`/sales/meti-aliases/${codeMeti}`),
    // Pivot / TCD
    pivot: (config) => api.post('/sales/pivot', config),
    listPresets: () => api.get('/sales/pivot/presets'),
    createPreset: (data) => api.post('/sales/pivot/presets', data),
    updatePreset: (id, data) => api.put(`/sales/pivot/presets/${id}`, data),
    deletePreset: (id) => api.delete(`/sales/pivot/presets/${id}`),
    // Gmail
    gmailTest: () => api.get('/sales/gmail/test'),
    gmailSync: (since) => api.post(`/sales/gmail/sync?since=${encodeURIComponent(since)}`),
    gmailStatus: () => api.get('/sales/gmail/status'),
    gmailClearProcessed: () => api.delete('/sales/gmail/processed'),
    // Financial
    financialSummary: () => api.get('/sales/financial-summary'),
    dailyCa: (params) => api.get('/sales/daily-ca', { params }),
    missingDataExport: () => api.get('/sales/missing-data-export', { responseType: 'blob' }),
    productEvolution: (params) => api.get('/sales/product-evolution', { params }),
    // Notes
    listNotes: (params) => api.get('/sales/notes', { params }),
    createNote: (data) => api.post('/sales/notes', data),
    updateNote: (id, data) => api.put(`/sales/notes/${id}`, data),
    deleteNote: (id) => api.delete(`/sales/notes/${id}`),
};

export const forecastAPI = {
    ca: (params) => api.get('/forecast/ca', { params }),
    recalculate: () => api.post('/forecast/recalculate'),
    staffingSettings: () => api.get('/forecast/staffing-settings'),
    saveStaffingSettings: (siteId, data) => api.put(`/forecast/staffing-settings/${siteId}`, data),
    staffingWeek: (params) => api.get('/forecast/staffing-week', { params }),
    accuracy: (params) => api.get('/forecast/accuracy', { params }),
};

export const orderStatsAPI = {
    productRatios: (params) => api.get('/order-stats/product-ratios', { params }),
};

export const orderFormsAPI = {
    templates: (params) => api.get('/order-forms/templates', { params }),
    saveTemplate: (data) => api.post('/order-forms/templates', data),
    deleteTemplate: (id) => api.delete(`/order-forms/templates/${id}`),
    pdfUrl: (id) => `${API_BASE}/order-forms/templates/${id}/pdf`,
    importPhoto: (templateId, files) => {
        const fd = new FormData();
        fd.append('template_id', templateId);
        files.forEach(f => fd.append('files', f));
        return api.post('/order-forms/import-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
    },
};

export const productionAPI = {
    clients: () => api.get('/production/clients'),
    products: () => api.get('/production/products'),
    createProduct: (data) => api.post('/production/products', data),
    setClientProducts: (clientId, product_ids, month) => api.patch(`/production/clients/${clientId}/products`, { product_ids, month }),
    grid: (params) => api.get('/production/grid', { params }),
    months: () => api.get('/production/months'),
    setCell: (data) => api.patch('/production/cell', data),
    setCells: (data) => api.patch('/production/cells', data),
    matchRecipes: () => api.post('/production/match-recipes'),
    syncRecipes: () => api.post('/production/sync-recipes'),
    linkRecipe: (productId, recipe_id) => api.patch(`/production/products/${productId}`, { recipe_id }),
    renameProduct: (productId, name) => api.patch(`/production/products/${productId}`, { name }),
    deleteProduct: (productId, force = false) => api.delete(`/production/products/${productId}${force ? '?force=true' : ''}`),
    recipesList: () => api.get('/recipes'),
    setDayBl: (data) => api.patch('/production/day-bl', data),
    createNextMonth: (source_month) => api.post('/production/create-next-month', { source_month }),
    dailySheet: (date) => api.get('/production/daily-sheet', { params: { date } }),
    reorderProducts: (product_ids) => api.patch('/production/products-reorder', { product_ids }),
    reorderClients: (client_ids) => api.patch('/production/clients-reorder', { client_ids }),
    renumberBls: (month) => api.post('/production/renumber-bls', { month }),
    clientsAll: () => api.get('/production/clients/all'),
    createClient: (data) => api.post('/production/clients', data),
    updateClient: (id, data) => api.put(`/production/clients/${id}`, data),
    setClientActive: (id, active) => api.patch(`/production/clients/${id}/active`, { active }),
    deleteMonth: (month) => api.post('/production/delete-month', { month }),
    listBackups: () => api.get('/production/backups'),
    restoreBackup: (id) => api.post(`/production/backups/${id}/restore`),
    deleteBackup: (id) => api.delete(`/production/backups/${id}`),
    salesSummary: (start) => api.get('/production/sales-summary', { params: { start } }),
    salesDaily: (start_date, end_date) => api.get('/production/sales-daily', { params: { start_date, end_date } }),
    salesFinancialSummary: () => api.get('/production/sales-financial-summary'),
    salesProductDetail: (params) => api.get('/production/sales-product-detail', { params }),
    sheetOrder: (kind, names) => api.patch('/production/sheet-order', { kind, names }),
    sheetBlock: (name, block) => api.patch('/production/sheet-block', { name, block }),
    sheetHeaders: (values) => api.patch('/production/sheet-headers', values),
    billing: (month) => api.get('/production/billing', { params: { month } }),
    printAvailability: (start, end) => api.get('/production/print-availability', { params: { start, end } }),
    blData: (clientId, date) => api.get('/production/bl-data', { params: { client_id: clientId, date } }),
    setMonthLock: (data) => api.put('/production/lock', data),
    clearCorrection: (data) => api.post('/production/clear-correction', data),
    importExcel: (formData) => api.post('/production/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }),
    dailySheetPdfUrl: (date) => `/production/daily-sheet-pdf?date=${date}`,
    blPdfUrl: (clientId, date) => `/production/bl-pdf?client_id=${clientId}&date=${date}`,
    billingPdfUrl: (month) => `/production/billing-pdf?month=${month}`,
};

export const ladditionAPI = {
    sync: (days = 7) => api.post(`/laddition/sync?days=${days}`),
    reports: (params) => api.get('/laddition/reports', { params }),
    reportDetail: (id) => api.get(`/laddition/reports/${id}`),
    summary: (params) => api.get('/laddition/summary', { params }),
    dailyCa: (start_date, end_date) => api.get('/laddition/daily-ca', { params: { start_date, end_date } }),
    financialSummary: () => api.get('/laddition/financial-summary'),
    importExcel: (formData) => api.post('/laddition/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
    importZPdf: (formData) => api.post('/laddition/import-z-pdf', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
    zAlert: () => api.get('/laddition/z-alert'),
};

export const vitrineAPI = {
    sync: (params) => api.post('/vitrine/sync', null, { params }),
    status: () => api.get('/vitrine/status'),
    summary: (params) => api.get('/vitrine/summary', { params }),
    day: (params) => api.get('/vitrine/day', { params }),
    alerts: (params) => api.get('/vitrine/alerts', { params }),
};

export const payrollAPI = {
    week: (start) => api.get('/payroll/week', { params: { start } }),
    saveWeek: (entries) => api.put('/payroll/week', { entries }),
    month: (year, month) => api.get('/payroll/month', { params: { year, month } }),
    getSettings: () => api.get('/payroll/settings'),
    saveSettings: (data) => api.put('/payroll/settings', data),
    employees: () => api.get('/payroll/employees'),
    createEmployee: (data) => api.post('/payroll/employees', data),
    updateEmployee: (id, data) => api.put(`/payroll/employees/${id}`, data),
    deleteEmployee: (id) => api.delete(`/payroll/employees/${id}`),
    primes: (year, month) => api.get('/payroll/primes', { params: { year, month } }),
    savePrimes: (year, month, entries) => api.put('/payroll/primes', { year, month, entries }),
    copyPrimes: (year, month, mode) => api.post('/payroll/primes/copy', { year, month, mode }),
    reorderEmployees: (ordered_ids) => api.post('/payroll/employees/reorder', { ordered_ids }),
    comboAnalyze: (raw) => api.post('/payroll-import/combo/analyze', { raw }),
    comboConfirm: (entries, mapping) => api.post('/payroll-import/combo/confirm', { entries, mapping }),
};

export const phoneCaptureAPI = {
    createSession: (multiple = true) => api.post('/phone-capture/sessions', { multiple }),
    getPhotos: (sid, token, after = -1) => api.get(`/phone-capture/sessions/${sid}/photos`, { params: { t: token, after } }),
    deleteSession: (sid, token) => api.delete(`/phone-capture/sessions/${sid}`, { params: { t: token } }),
};

export const lossesAPI = {
    catalog: (params) => api.get('/losses/catalog', { params }),
    resolve: (params) => api.get('/losses/resolve', { params }),
    create: (data) => api.post('/losses', data),
    list: (params) => api.get('/losses', { params }),
    remove: (id) => api.delete(`/losses/${id}`),
    summary: (params) => api.get('/losses/summary', { params }),
    synthesis: (params) => api.get('/losses/synthesis', { params }),
    getNote: (params) => api.get('/losses/note', { params }),
    saveNote: (data) => api.put('/losses/note', data),
};

export const labelsAPI = {
    meta: () => api.get('/labels/meta'),
    templates: () => api.get('/labels/templates'),
    importTemplate: (clientId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/labels/templates/import?client_id=${clientId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    updateBindings: (id, bindings) => api.put(`/labels/templates/${id}/bindings`, { bindings }),
    deleteTemplate: (id) => api.delete(`/labels/templates/${id}`),
    data: (clientId) => api.get('/labels/data', { params: { client_id: clientId } }),
    saveData: (clientId, productId, payload) => api.put(`/labels/data/${productId}?client_id=${clientId}`, payload),
    importExcel: (clientId, templateId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/labels/data/import-excel?client_id=${clientId}${templateId ? `&template_id=${templateId}` : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    importLog: (clientId) => api.get('/labels/import-log', { params: { client_id: clientId } }),
    associate: (payload) => api.post('/labels/data/associate', payload),
    uploadTemplateImage: (templateId, objectName, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/labels/templates/${templateId}/image?object_name=${encodeURIComponent(objectName)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    layout: (payload) => api.post('/labels/layout', payload),
    saveTplAdjust: (templateId, adjustments) => api.put(`/labels/templates/${templateId}/adjustments`, { adjustments }),
    resetTplAdjust: (templateId) => api.delete(`/labels/templates/${templateId}/adjustments`),
    saveProdAdjust: (clientId, productId, adjustments) => api.put(`/labels/data-adjustments?client_id=${clientId}&product_id=${productId}`, { adjustments }),
    resetProdAdjust: (clientId, productId) => api.delete(`/labels/data-adjustments?client_id=${clientId}&product_id=${productId}`),
    dayQuantities: (clientId, date) => api.get('/labels/day-quantities', { params: { client_id: clientId, date } }),
    previewBlob: (clientId, productId, date) => api.get('/labels/preview', { params: { client_id: clientId, product_id: productId, date }, responseType: 'blob' }),
    print: (payload) => api.post('/labels/print', payload),
    jobs: () => api.get('/labels/jobs'),
    retryJob: (id) => api.post(`/labels/jobs/${id}/retry`),
    purgeJobs: () => api.delete('/labels/jobs'),
    agents: () => api.get('/labels/agents'),
    createAgent: () => api.post('/labels/agents'),
    deleteAgent: (id) => api.delete(`/labels/agents/${id}`),
};

export const complianceAPI = {
    overview: () => api.get('/compliance/overview'),
    updateItem: (id, data) => api.put(`/compliance/items/${id}`, data),
    addRegistry: (data) => api.post('/compliance/registry', data),
    updateRegistry: (id, data) => api.put(`/compliance/registry/${id}`, data),
    deleteRegistry: (id) => api.delete(`/compliance/registry/${id}`),
};
